import { makeAutoObservable } from "mobx";

import type { IAdmin } from "@/api/admin/auth";
import { LOGIN_TOKEN, REFRESH_TOKEN } from "@/api/constants";
import type { IRestaurant } from "@/api/restaurant";
import { getLogoutLoginPath } from "@/config/path";
import {
  cancelScheduledTokenRefresh,
  scheduleTokenRefresh,
} from "@/services/auth/tokenRefreshScheduler";
import {
  clearStoredTokens,
  getLegacyUserData,
  hasLegacyUserData,
  saveTokens,
} from "@/services/auth/tokenStorage";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";

import { rootStore } from "./RootStore";

export enum RestaurantRole {
  "GROUP" = "GROUP",
  "BRAND" = "BRAND",
  "BRANCH" = "BRANCH",
}

export enum StageAuth {
  "ONBOARDING" = "onboarding",
  "UNAUTHENTICATED" = "unauthenticated",
  "AUTHENTICATED" = "authenticated",
}

export enum AuthRole {
  ADMIN = "admin",
  RESTAURANT = "restaurant",
}

export const ADMIN_FLAG = "IS_ADMIN" as const;
export const legacyAdminRole = "SUPER_ADMIN";

export type RestaurantAccess = RestaurantRole | null;
export type AuthStage = StageAuth;
export type UserRole = AuthRole | null;

type TUser = IAdmin | IRestaurant;

export class AuthStore {
  stage: AuthStage = StageAuth.ONBOARDING;
  role: UserRole = null;
  restaurantAccess: RestaurantAccess = null;
  token: string | null = null;
  refreshToken: string | null = null;
  isInitialized = false;
  isLoggingOut = false;
  createdAt: Date | null = null;
  updatedAt: Date | null = null;
  email: string | null = null;
  firstName: string | null = null;
  lastName: string | null = null;
  phoneNumber: string | null = null;
  id?: string | null = null;

  constructor() {
    makeAutoObservable(this);
    this.hydrate();
  }

  hydrate() {
    const token = localStorage.getItem(LOGIN_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    const legacyUserData = getLegacyUserData();

    if (token) {
      this.token = token;
      this.refreshToken = refreshToken;
    }

    if (token && legacyUserData) {
      let isAdminFlag = localStorage.getItem(ADMIN_FLAG);
      const alternativeAdminFlag =
        legacyUserData.role === legacyAdminRole ? "true" : null;

      isAdminFlag =
        isAdminFlag === "true" || alternativeAdminFlag === "true"
          ? "true"
          : isAdminFlag;

      this.stage = StageAuth.AUTHENTICATED;
      this.createdAt = legacyUserData.createdAt
        ? new Date(legacyUserData.createdAt)
        : null;
      this.updatedAt = legacyUserData.updatedAt
        ? new Date(legacyUserData.updatedAt)
        : null;
      this.email = legacyUserData.email;
      this.firstName = legacyUserData.firstName;
      this.lastName = legacyUserData.lastName;
      this.phoneNumber = legacyUserData.phoneNumber;
      this.id = legacyUserData.id;

      if (isAdminFlag === "true") {
        this.role = AuthRole.ADMIN;
      } else if (Object.keys(RestaurantRole).includes(legacyUserData.role)) {
        this.role = AuthRole.RESTAURANT;
        this.restaurantAccess = legacyUserData.role as RestaurantRole;
      } else {
        this.role = AuthRole.RESTAURANT;
      }
    } else if (!token) {
      this.stage = StageAuth.UNAUTHENTICATED;
    }

    if (token && refreshToken) {
      scheduleTokenRefresh(token);
    }
  }

  setTokens(token: string, refreshToken: string) {
    this.token = token;
    this.refreshToken = refreshToken;
    saveTokens({ token, refreshToken });
    scheduleTokenRefresh(token);
  }

  setToken(token: string) {
    if (this.refreshToken) {
      this.setTokens(token, this.refreshToken);
      return;
    }

    this.token = token;
  }

  setInitialized() {
    this.isInitialized = true;
  }

  startLogout() {
    this.isLoggingOut = true;
  }

  finishLogout() {
    this.isLoggingOut = false;
  }

  completeOnboarding() {
    this.stage = StageAuth.UNAUTHENTICATED;
  }

  loginAsSuperAdmin(data: TUser) {
    this.setUserData(data);
    this.role = AuthRole.ADMIN;
    localStorage.setItem(ADMIN_FLAG, "true");
  }

  loginAsRestaurant(data: TUser) {
    this.stage = StageAuth.AUTHENTICATED;
    this.role = AuthRole.RESTAURANT;
    this.setUserData(data);
    this.restaurantAccess = data.role as RestaurantRole;
  }

  setUserData(data: TUser) {
    this.stage = StageAuth.AUTHENTICATED;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : null;
    this.email = data.email;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.phoneNumber = data.phoneNumber;
    this.id = data.id;
  }

  clearSession() {
    this.token = null;
    this.refreshToken = null;
    this.stage = StageAuth.UNAUTHENTICATED;
    this.role = null;
    this.restaurantAccess = null;
    this.createdAt = null;
    this.updatedAt = null;
    this.email = null;
    this.firstName = null;
    this.lastName = null;
    this.phoneNumber = null;
    this.id = null;
    cancelScheduledTokenRefresh();
    clearStoredTokens();
  }

  logout() {
    const loginPath = getLogoutLoginPath(
      this.isAdmin,
      window.location.pathname
    );

    cancelScheduledTokenRefresh();
    clearStoredTokens();
    rootStore.restaurantAccessStore.reset();
    rootStore.adminAccessStore.reset();

    window.location.href = loginPath;
  }

  get isLegacySession() {
    return hasLegacyUserData();
  }

  get isAdmin() {
    return (
      this.stage === StageAuth.AUTHENTICATED && this.role === AuthRole.ADMIN
    );
  }

  get isRestaurant() {
    return (
      this.stage === StageAuth.AUTHENTICATED &&
      this.role === AuthRole.RESTAURANT
    );
  }

  get isGroupAccess() {
    return this.isRestaurant && this.restaurantAccess === RestaurantRole.GROUP;
  }

  get isBrandAccess() {
    return this.isRestaurant && this.restaurantAccess === RestaurantRole.BRAND;
  }

  get isBranchAccess() {
    return this.isRestaurant && this.restaurantAccess === RestaurantRole.BRANCH;
  }

  get isOnboarding() {
    return this.stage === StageAuth.ONBOARDING;
  }

  get isUnauthenticated() {
    return this.stage === StageAuth.UNAUTHENTICATED;
  }

  get currentRestaurantAccess(): RestaurantAccess {
    return this.restaurantAccess;
  }

  get currentStage() {
    if (this.stage === StageAuth.ONBOARDING) {
      return this.stage;
    }
    return this.role;
  }

  get userFullName(): string {
    if (this.firstName || this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    return DEFAULT_NAME;
  }
}
